/**
 * Sales API service with TanStack React Query hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost, isTokenGetterSet } from './client'
import { assetKeys } from './assets'
import type { SaleTransaction, CreateSaleTransactionInput } from '@/types/sale'

/**
 * Query keys for sales
 */
export const saleKeys = {
  all: ['sales'] as const,
  lists: () => [...saleKeys.all, 'list'] as const,
  list: (filters?: { assetId?: string }) => [...saleKeys.lists(), filters] as const,
  details: () => [...saleKeys.all, 'detail'] as const,
  detail: (id: string) => [...saleKeys.details(), id] as const,
}

/**
 * Fetch all sales for the current user
 */
export function useSales(assetId?: string) {
  return useQuery({
    queryKey: saleKeys.list(assetId ? { assetId } : undefined),
    queryFn: async () => {
      const params = assetId ? `?assetId=${assetId}` : ''
      const response = await apiGet<{ data: SaleTransaction[] }>(`/api/sales${params}`)
      return response.data
    },
    enabled: isTokenGetterSet(), // Only run when token getter is set
  })
}

/**
 * Create a new sale transaction
 */
export function useCreateSale() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateSaleTransactionInput) => {
      const response = await apiPost<{ data: SaleTransaction; message: string }>('/api/sales', input)
      return response.data
    },
    onSuccess: () => {
      // Invalidate and refetch sales list
      queryClient.invalidateQueries({ queryKey: saleKeys.lists() })
      // Also invalidate assets since sale updates asset status/quantity
      queryClient.invalidateQueries({ queryKey: assetKeys.lists() })
    },
  })
}

